import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import type {
  Activity,
  CrmState,
  DashboardLayout,
  InterestedProperty,
  Lead,
  MasterData,
  Property,
  Requirement,
  UserProfile,
  Viewing
} from "../types";
import { runtime, isSupabaseConfigured, oauthRedirectUrl } from "./runtime";
import { id, todayIso } from "./utils";

const STORAGE_KEY = "home-lovin-react-crm-state-v1";

const COLLECTIONS = {
  leads: "lead",
  requirements: "requirement",
  properties: "property",
  interestedProperties: "interested_property",
  viewings: "viewing",
  activities: "activity",
  opportunities: "opportunity",
  commissions: "commission",
  dashboardLayouts: "dashboard_layout",
  notifications: "notification",
  auditLogs: "audit"
} as const;

const REVERSE_COLLECTIONS = Object.fromEntries(Object.entries(COLLECTIONS).map(([collection, entity]) => [entity, collection])) as Record<string, keyof typeof COLLECTIONS>;

export const masterDefaults: MasterData = {
  propertyPurposes: ["Buy", "Rent", "Rent to Buy"],
  propertyDevelopments: ["Narai Property", "Riverside Villa Estate", "The Residence"],
  propertyTypes: ["Condo", "House", "Townhome", "Villa", "Land", "Office", "Commercial", "Other"],
  propertyStatuses: ["Available", "Interested", "Viewing", "Reserved", "Sold-out"],
  propertySources: ["Owner Direct", "Developer", "Agent Co-broke", "Portal", "Referral"],
  leadSources: ["Website", "Referral", "Walk-in", "Facebook", "LINE", "Portal"],
  leadStatuses: ["New", "Contacted", "Qualified", "Viewing Scheduled", "Negotiating", "Closed Won", "Closed Lost"],
  leadTemperatures: ["Hot", "Warm", "Cold"],
  nationalities: ["Thai", "Chinese", "Japanese", "Singaporean", "American", "British", "Other"],
  btsStations: ["Thong Lo", "Ekkamai", "Phrom Phong", "Asok", "Siam", "Chit Lom"],
  mrtStations: ["Sukhumvit", "Queen Sirikit", "Lumphini", "Silom", "Thailand Cultural Centre"],
  airportLinkStations: ["Phaya Thai", "Makkasan", "Ramkhamhaeng", "Suvarnabhumi"],
  airports: ["Suvarnabhumi", "Don Mueang"],
  facilities: ["Pool", "Fitness", "Parking", "Pet Friendly", "BTS/MRT Access", "Co-working space", "Garden"],
  roomTypes: ["Bedroom", "Bathroom", "Living room", "Kitchen", "Other room"],
  parkingTypes: ["Car", "Motorcycle", "Bicycle"],
  interestStatuses: ["Interested", "Shared with client", "Viewing scheduled", "Viewed", "Offer made", "Negotiating", "Won", "Lost"],
  scheduleTypes: ["Property viewing", "Follow-up call", "Online meeting", "Document collection", "Offer discussion", "Contract signing", "Internal task"],
  scheduleStatuses: ["Scheduled", "Completed", "Cancelled", "No Show", "Rescheduled"],
  viewingStatuses: ["Scheduled", "Completed", "Cancelled", "No Show", "Rescheduled"],
  activityTypes: ["Call", "LINE", "WhatsApp", "Email", "Meeting", "Property shared", "Document sent", "Offer discussed"],
  activityStatuses: ["Open", "Completed", "Overdue"],
  pipelineStages: ["Qualified", "Viewing", "Interested", "Offer", "Negotiation", "Closing", "Won", "Lost"],
  paymentStatuses: ["Pending", "Approved", "Paid"],
  teams: ["Sukhumvit Team", "Riverside Team", "Suburban Team"],
  userRoles: ["Admin", "Manager", "Agent"]
};

export const defaultDashboardWidgets: DashboardLayout["widgets"] = ["conversion", "commission", "topLeads", "topProperties", "schedule", "activity"];

export function defaultState(): CrmState {
  const now = new Date().toISOString();
  const admin: UserProfile = {
    id: "usr-admin",
    name: "Admin User",
    email: "admin@homelovin.local",
    role: "Admin",
    active: true,
    teamId: "Sukhumvit Team",
    teamIds: ["Sukhumvit Team", "Riverside Team", "Suburban Team"],
    createdAt: now
  };
  const manager: UserProfile = {
    id: "usr-manager",
    name: "Mali Manager",
    email: "manager@homelovin.local",
    role: "Manager",
    active: true,
    teamId: "Sukhumvit Team",
    teamIds: ["Sukhumvit Team"],
    createdAt: now
  };
  const agent: UserProfile = {
    id: "usr-agent",
    name: "Nara Agent",
    email: "agent@homelovin.local",
    role: "Agent",
    active: true,
    teamId: "Sukhumvit Team",
    teamIds: ["Sukhumvit Team"],
    managerId: manager.id,
    createdAt: now
  };
  const otherAgent: UserProfile = {
    id: "usr-river",
    name: "Kiet Riverside",
    email: "kiet@homelovin.local",
    role: "Agent",
    active: true,
    teamId: "Riverside Team",
    teamIds: ["Riverside Team"],
    createdAt: now
  };

  return normalizeState({
    users: [admin, manager, agent, otherAgent],
    leads: [
      {
        id: "lead-demo",
        name: "Anong Chai",
        phone: "081-234-5678",
        email: "anong@example.com",
        nationality: "Thai",
        source: "Website",
        purpose: "Buy",
        budget: 8500000,
        timeline: "1-3 months",
        temperature: "Hot",
        status: "Qualified",
        note: "Looking for a pet-friendly condo near BTS for a young family.",
        agentId: agent.id,
        teamId: agent.teamId,
        active: true,
        createdBy: agent.id,
        updatedBy: agent.id,
        createdAt: now,
        updatedAt: now
      },
      {
        id: "lead-demo-2",
        name: "Daniel Wong",
        phone: "089-555-1212",
        email: "daniel@example.com",
        nationality: "Singaporean",
        source: "Referral",
        purpose: "Rent",
        budget: 125000,
        timeline: "This month",
        temperature: "Warm",
        status: "New",
        note: "Executive rental near the river, prefers serviced-style facilities.",
        agentId: otherAgent.id,
        teamId: otherAgent.teamId,
        active: true,
        createdBy: otherAgent.id,
        updatedBy: otherAgent.id,
        createdAt: now,
        updatedAt: now
      }
    ],
    requirements: [
      {
        id: "req-demo",
        leadId: "lead-demo",
        purpose: "Buy",
        location: "Bangkok",
        preferredAreas: "Thonglor, Ekkamai, Phrom Phong",
        nearBts: "Thong Lo, Ekkamai",
        nearMrt: "Sukhumvit",
        nearAirportLink: "",
        nearAirport: "",
        propertyType: "Condo",
        development: "",
        budgetMin: 6000000,
        budgetMax: 9000000,
        currency: "THB",
        bedrooms: 2,
        bathrooms: 2,
        livingRooms: 1,
        kitchens: 1,
        otherRooms: 0,
        size: 60,
        parkingCars: 1,
        parkingMotorcycles: 0,
        parkingBicycles: 0,
        petFriendly: true,
        transitAccess: true,
        pool: true,
        fitness: true,
        coWorkingSpace: false,
        garden: false,
        facilities: "Pool, Fitness, Parking",
        timeline: "1-3 months",
        updatedAt: now
      },
      {
        id: "req-demo-2",
        leadId: "lead-demo-2",
        purpose: "Rent",
        location: "Bangkok",
        preferredAreas: "Charoen Krung, Sathorn",
        nearBts: "Saphan Taksin",
        nearMrt: "",
        nearAirportLink: "",
        nearAirport: "",
        propertyType: "Villa",
        development: "",
        budgetMin: 90000,
        budgetMax: 160000,
        currency: "THB",
        bedrooms: 3,
        bathrooms: 3,
        livingRooms: 1,
        kitchens: 1,
        otherRooms: 1,
        size: 180,
        parkingCars: 2,
        parkingMotorcycles: 0,
        parkingBicycles: 0,
        petFriendly: false,
        transitAccess: false,
        pool: true,
        fitness: false,
        coWorkingSpace: false,
        garden: true,
        facilities: "Garden, Pool, Parking",
        timeline: "This month",
        updatedAt: now
      }
    ],
    properties: [
      {
        id: "prop-demo-1",
        code: "HL-1001",
        name: "The Residence Thonglor",
        purpose: "Buy",
        type: "Condo",
        development: "The Residence",
        building: "A",
        floor: "18",
        location: "Thonglor, Bangkok",
        nearBts: "Thong Lo",
        nearMrt: "Sukhumvit",
        nearAirportLink: "",
        nearAirport: "",
        source: "Developer",
        status: "Available",
        originalPrice: 8500000,
        promotionPrice: 8200000,
        price: 8200000,
        currency: "THB",
        size: 64,
        bedrooms: 2,
        bathrooms: 2,
        livingRooms: 1,
        kitchens: 1,
        otherRooms: 0,
        parkingCars: 1,
        parkingMotorcycles: 0,
        parkingBicycles: 1,
        petFriendly: true,
        pool: true,
        view: "Pool",
        fitness: true,
        coWorkingSpace: true,
        garden: false,
        facilities: "Pool, Fitness, Parking, BTS/MRT Access, Pet Friendly, Co-working space",
        photos: "",
        timeline: "Ready to transfer",
        commissionRate: 3,
        commissionAmount: 246000,
        affiliateLink: "",
        soldOutAgentId: "",
        publicToken: "pub-thonglor-1001",
        publicVisible: true,
        visibility: "Public",
        internalNote: "Developer promo expires end of quarter.",
        active: true,
        createdBy: admin.id,
        updatedBy: admin.id,
        createdAt: now,
        updatedAt: now
      },
      {
        id: "prop-demo-2",
        code: "HL-1002",
        name: "Riverside Villa Estate",
        purpose: "Rent",
        type: "Villa",
        development: "Riverside Villa Estate",
        building: "Villa 4",
        floor: "2",
        location: "Charoen Krung, Bangkok",
        nearBts: "Saphan Taksin",
        nearMrt: "",
        nearAirportLink: "",
        nearAirport: "",
        source: "Owner Direct",
        status: "Available",
        originalPrice: 150000,
        promotionPrice: 135000,
        price: 135000,
        currency: "THB",
        size: 280,
        bedrooms: 4,
        bathrooms: 5,
        livingRooms: 2,
        kitchens: 1,
        otherRooms: 1,
        parkingCars: 2,
        parkingMotorcycles: 1,
        parkingBicycles: 2,
        petFriendly: false,
        pool: true,
        view: "Garden, Pool",
        fitness: false,
        coWorkingSpace: false,
        garden: true,
        facilities: "Garden, Pool, Parking",
        photos: "",
        timeline: "Available now",
        commissionRate: 1,
        commissionAmount: 1350,
        affiliateLink: "",
        soldOutAgentId: "",
        publicToken: "pub-riverside-1002",
        publicVisible: true,
        visibility: "Public",
        internalNote: "Owner prefers one-year contract.",
        active: true,
        createdBy: admin.id,
        updatedBy: admin.id,
        createdAt: now,
        updatedAt: now
      }
    ],
    interestedProperties: [
      {
        id: "int-demo",
        leadId: "lead-demo",
        propertyId: "prop-demo-1",
        agentId: agent.id,
        teamId: agent.teamId,
        status: "Shared with client",
        note: "Client likes the building and wants a weekend viewing.",
        createdAt: now,
        updatedAt: now
      }
    ],
    viewings: [
      {
        id: "view-demo",
        type: "Property viewing",
        leadId: "lead-demo",
        propertyId: "prop-demo-1",
        agentId: agent.id,
        startsAt: `${todayIso()}T14:00`,
        location: "The Residence Thonglor lobby",
        status: "Scheduled",
        note: "Confirm visitor parking before noon."
      }
    ],
    activities: [
      {
        id: "act-demo",
        leadId: "lead-demo",
        propertyId: "prop-demo-1",
        agentId: agent.id,
        type: "WhatsApp",
        dueAt: `${todayIso()}T10:30`,
        status: "Open",
        note: "Send top 3 matches and confirm viewing."
      }
    ],
    opportunities: [
      { id: "opp-demo", leadId: "lead-demo", propertyId: "prop-demo-1", agentId: agent.id, stage: "Interested", value: 8200000 }
    ],
    commissions: [
      { id: "com-demo", opportunityId: "opp-demo", propertyValue: 8200000, commissionRate: 3, commissionAmount: 246000, paymentStatus: "Pending" }
    ],
    dashboardLayouts: [
      { id: "layout-admin", userId: admin.id, widgets: defaultDashboardWidgets, hiddenWidgets: [], updatedAt: now },
      { id: "layout-agent", userId: agent.id, widgets: defaultDashboardWidgets, hiddenWidgets: [], updatedAt: now }
    ],
    notifications: [
      { id: "note-demo", userId: agent.id, type: "followup_due", message: "Follow-up due today for Anong Chai", read: false, createdAt: now }
    ],
    auditLogs: [],
    masterData: structuredClone(masterDefaults)
  });
}

function normalizeText(value: unknown, fallback = "") {
  return String(value ?? fallback);
}

function normalizeNumber(value: unknown, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function normalizeBoolean(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeList(values: string[]) {
  return Array.from(new Set(values.map((value) => normalizeText(value).trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).join(", ");
}

function normalizeUser(user: Partial<UserProfile>, now: string): UserProfile {
  const teamId = normalizeText(user.teamId, "Sukhumvit Team");
  return {
    id: normalizeText(user.id, id("usr")),
    name: normalizeText(user.name || user.email, "User"),
    email: normalizeText(user.email),
    role: user.role || "Agent",
    active: user.active !== false,
    teamId,
    teamIds: Array.isArray(user.teamIds) && user.teamIds.length ? user.teamIds.map(String) : [teamId],
    managerId: user.managerId,
    avatarUrl: user.avatarUrl,
    createdAt: user.createdAt || now,
    lastLoginAt: user.lastLoginAt
  };
}

function normalizeLead(lead: Partial<Lead>, now: string): Lead {
  const agentId = normalizeText(lead.agentId, "usr-agent");
  return {
    id: normalizeText(lead.id, id("lead")),
    name: normalizeText(lead.name),
    phone: normalizeText(lead.phone),
    email: normalizeText(lead.email),
    nationality: normalizeText(lead.nationality, "Thai"),
    source: normalizeText(lead.source, "Website"),
    purpose: normalizeText(lead.purpose, "Buy"),
    budget: normalizeNumber(lead.budget),
    timeline: normalizeText(lead.timeline),
    temperature: lead.temperature || "Warm",
    status: normalizeText(lead.status, "New"),
    note: normalizeText(lead.note),
    agentId,
    teamId: normalizeText(lead.teamId, "Sukhumvit Team"),
    active: lead.active !== false,
    createdBy: lead.createdBy || agentId,
    updatedBy: lead.updatedBy || agentId,
    createdAt: lead.createdAt || now,
    updatedAt: lead.updatedAt || now
  };
}

function normalizeRequirement(requirement: Partial<Requirement>, now: string): Requirement {
  return {
    id: normalizeText(requirement.id, id("req")),
    leadId: normalizeText(requirement.leadId),
    purpose: normalizeText(requirement.purpose, "Buy"),
    location: normalizeText(requirement.location, "Bangkok"),
    preferredAreas: normalizeText(requirement.preferredAreas),
    nearBts: normalizeText(requirement.nearBts),
    nearMrt: normalizeText(requirement.nearMrt),
    nearAirportLink: normalizeText(requirement.nearAirportLink),
    nearAirport: normalizeText(requirement.nearAirport),
    propertyType: normalizeText(requirement.propertyType, "Condo"),
    development: normalizeText(requirement.development),
    budgetMin: normalizeNumber(requirement.budgetMin),
    budgetMax: normalizeNumber(requirement.budgetMax),
    currency: normalizeText(requirement.currency, "THB"),
    bedrooms: normalizeNumber(requirement.bedrooms, 1),
    bathrooms: normalizeNumber(requirement.bathrooms, 1),
    livingRooms: normalizeNumber(requirement.livingRooms, 1),
    kitchens: normalizeNumber(requirement.kitchens, 1),
    otherRooms: normalizeNumber(requirement.otherRooms),
    size: normalizeNumber(requirement.size),
    parkingCars: normalizeNumber(requirement.parkingCars),
    parkingMotorcycles: normalizeNumber(requirement.parkingMotorcycles),
    parkingBicycles: normalizeNumber(requirement.parkingBicycles),
    petFriendly: normalizeBoolean(requirement.petFriendly),
    transitAccess: requirement.transitAccess !== false,
    pool: normalizeBoolean(requirement.pool),
    fitness: normalizeBoolean(requirement.fitness),
    coWorkingSpace: normalizeBoolean(requirement.coWorkingSpace),
    garden: normalizeBoolean(requirement.garden),
    facilities: normalizeText(requirement.facilities),
    timeline: normalizeText(requirement.timeline),
    updatedAt: requirement.updatedAt || now
  };
}

function normalizeProperty(property: Partial<Property>, now: string): Property {
  const promotionPrice = normalizeNumber(property.promotionPrice || property.price || property.originalPrice);
  const originalPrice = normalizeNumber(property.originalPrice || property.price || promotionPrice);
  const price = normalizeNumber(property.price || promotionPrice || originalPrice);
  const commissionRate = normalizeNumber(property.commissionRate);
  const view = normalizeList([property.view || "", property.poolView ? "Pool" : "", property.gardenView ? "Garden" : ""]);
  return {
    id: normalizeText(property.id, id("prop")),
    code: normalizeText(property.code, `HL-${Math.floor(1000 + Math.random() * 9000)}`),
    name: normalizeText(property.name),
    purpose: normalizeText(property.purpose, "Buy"),
    type: normalizeText(property.type, "Condo"),
    development: normalizeText(property.development || property.name),
    building: normalizeText(property.building),
    floor: normalizeText(property.floor),
    location: normalizeText(property.location),
    nearBts: normalizeText(property.nearBts),
    nearMrt: normalizeText(property.nearMrt),
    nearAirportLink: normalizeText(property.nearAirportLink),
    nearAirport: normalizeText(property.nearAirport),
    source: normalizeText(property.source, "Owner Direct"),
    status: property.status === "On-sale" ? "Available" : property.status === "Sold Out" ? "Sold-out" : normalizeText(property.status, "Available"),
    originalPrice,
    promotionPrice,
    price,
    currency: normalizeText(property.currency, "THB"),
    size: normalizeNumber(property.size),
    bedrooms: normalizeNumber(property.bedrooms, 1),
    bathrooms: normalizeNumber(property.bathrooms, 1),
    livingRooms: normalizeNumber(property.livingRooms, 1),
    kitchens: normalizeNumber(property.kitchens, 1),
    otherRooms: normalizeNumber(property.otherRooms),
    parkingCars: normalizeNumber(property.parkingCars),
    parkingMotorcycles: normalizeNumber(property.parkingMotorcycles),
    parkingBicycles: normalizeNumber(property.parkingBicycles),
    petFriendly: normalizeBoolean(property.petFriendly) || normalizeText(property.facilities).toLowerCase().includes("pet"),
    pool: normalizeBoolean(property.pool) || normalizeText(property.facilities).toLowerCase().includes("pool"),
    view,
    poolView: normalizeText(property.poolView),
    fitness: normalizeBoolean(property.fitness) || normalizeText(property.facilities).toLowerCase().includes("fitness") || normalizeText(property.facilities).toLowerCase().includes("gym"),
    coWorkingSpace: normalizeBoolean(property.coWorkingSpace) || normalizeText(property.facilities).toLowerCase().includes("co-working"),
    garden: normalizeBoolean(property.garden) || normalizeText(property.facilities).toLowerCase().includes("garden"),
    gardenView: normalizeText(property.gardenView),
    facilities: normalizeText(property.facilities),
    photos: normalizeText(property.photos),
    timeline: normalizeText(property.timeline),
    commissionRate,
    commissionAmount: normalizeNumber(property.commissionAmount, Math.round((price * commissionRate) / 100)),
    affiliateLink: normalizeText(property.affiliateLink),
    soldOutAgentId: property.soldOutAgentId,
    publicToken: normalizeText(property.publicToken),
    publicVisible: normalizeBoolean(property.publicVisible),
    visibility: property.visibility || (property.publicVisible ? "Public" : "Private"),
    internalNote: normalizeText(property.internalNote),
    active: property.active !== false,
    createdBy: property.createdBy,
    updatedBy: property.updatedBy,
    createdAt: property.createdAt || now,
    updatedAt: property.updatedAt || now
  };
}

function normalizeViewing(viewing: Partial<Viewing>, now: string): Viewing {
  return {
    id: normalizeText(viewing.id, id("view")),
    type: normalizeText(viewing.type, "Property viewing"),
    leadId: normalizeText(viewing.leadId),
    propertyId: normalizeText(viewing.propertyId),
    agentId: normalizeText(viewing.agentId),
    startsAt: normalizeText(viewing.startsAt, now),
    location: normalizeText(viewing.location),
    status: viewing.status === "Planned" ? "Scheduled" : normalizeText(viewing.status, "Scheduled"),
    note: normalizeText(viewing.note)
  };
}

function normalizeActivity(activity: Partial<Activity>, now: string): Activity {
  return {
    id: normalizeText(activity.id, id("act")),
    leadId: normalizeText(activity.leadId),
    requirementId: activity.requirementId,
    propertyId: activity.propertyId,
    agentId: normalizeText(activity.agentId),
    type: normalizeText(activity.type, "Call"),
    dueAt: normalizeText(activity.dueAt, now),
    status: normalizeText(activity.status, "Open"),
    note: normalizeText(activity.note),
    completedAt: activity.completedAt
  };
}

function normalizeInterest(interest: Partial<InterestedProperty>, now: string): InterestedProperty {
  return {
    id: normalizeText(interest.id, id("int")),
    leadId: normalizeText(interest.leadId),
    propertyId: normalizeText(interest.propertyId),
    agentId: normalizeText(interest.agentId),
    teamId: normalizeText(interest.teamId),
    status: normalizeText(interest.status, "Interested"),
    note: normalizeText(interest.note),
    createdAt: interest.createdAt || now,
    updatedAt: interest.updatedAt || now
  };
}

function normalizeDashboardLayout(layout: Partial<DashboardLayout>, now: string): DashboardLayout {
  return {
    id: normalizeText(layout.id, id("layout")),
    userId: normalizeText(layout.userId),
    widgets: Array.isArray(layout.widgets) && layout.widgets.length ? layout.widgets : defaultDashboardWidgets,
    hiddenWidgets: Array.isArray(layout.hiddenWidgets) ? layout.hiddenWidgets : [],
    updatedAt: layout.updatedAt || now
  };
}

export function normalizeState(input: Partial<CrmState> | null | undefined): CrmState {
  const now = new Date().toISOString();
  const state = { ...input } as Partial<CrmState>;
  const normalized: CrmState = {
    users: (state.users || []).map((user) => normalizeUser(user, now)),
    leads: (state.leads || []).map((lead) => normalizeLead(lead, now)),
    requirements: (state.requirements || []).map((requirement) => normalizeRequirement(requirement, now)),
    properties: (state.properties || []).map((property) => normalizeProperty(property, now)),
    interestedProperties: (state.interestedProperties || []).map((interest) => normalizeInterest(interest, now)),
    viewings: (state.viewings || []).map((viewing) => normalizeViewing(viewing, now)),
    activities: (state.activities || []).map((activity) => normalizeActivity(activity, now)),
    opportunities: state.opportunities || [],
    commissions: state.commissions || [],
    dashboardLayouts: (state.dashboardLayouts || []).map((layout) => normalizeDashboardLayout(layout, now)),
    notifications: state.notifications || [],
    auditLogs: state.auditLogs || [],
    masterData: { ...masterDefaults, ...(state.masterData || {}) }
  };
  normalized.masterData.propertyStatuses = masterDefaults.propertyStatuses;

  if (!normalized.users.length && input !== null) return defaultState();

  const knownLeadIds = new Set(normalized.leads.map((lead) => lead.id));
  normalized.requirements = normalized.requirements.filter((requirement) => knownLeadIds.has(requirement.leadId));
  for (const lead of normalized.leads) {
    if (!normalized.requirements.some((requirement) => requirement.leadId === lead.id)) {
      normalized.requirements.push(normalizeRequirement({ id: id("req"), leadId: lead.id, purpose: lead.purpose, budgetMax: lead.budget, timeline: lead.timeline }, now));
    }
  }

  return normalized;
}

function localLoad() {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? normalizeState(JSON.parse(raw) as Partial<CrmState>) : defaultState();
}

function localSave(state: CrmState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

let client: SupabaseClient | null = null;

export function getSupabase() {
  if (!isSupabaseConfigured()) return null;
  if (!client) {
    client = createClient(runtime.supabaseUrl, runtime.supabaseAnonKey, {
      auth: { autoRefreshToken: true, detectSessionInUrl: true, persistSession: true }
    });
  }
  return client;
}

function mapProfile(row: Record<string, unknown>): UserProfile {
  const teamId = String(row.team_id || "Sukhumvit Team");
  return {
    id: String(row.id),
    name: String(row.full_name || row.email || "User"),
    email: String(row.email || ""),
    role: (row.role as UserProfile["role"]) || "Agent",
    active: row.active !== false,
    teamId,
    teamIds: Array.isArray(row.team_ids) && row.team_ids.length ? row.team_ids.map(String) : [teamId],
    managerId: row.manager_id ? String(row.manager_id) : undefined,
    avatarUrl: row.avatar_url ? String(row.avatar_url) : undefined,
    createdAt: row.created_at ? String(row.created_at) : undefined,
    lastLoginAt: row.last_login_at ? String(row.last_login_at) : undefined
  };
}

function profileRow(user: UserProfile) {
  return {
    id: user.id,
    email: user.email,
    full_name: user.name,
    role: user.role,
    active: user.active,
    team_id: user.teamId,
    team_ids: user.teamIds?.length ? user.teamIds : [user.teamId]
  };
}

async function ensureProfile(session: Session) {
  const supabase = getSupabase();
  if (!supabase) return null;
  const user = session.user;
  if (!user.email_confirmed_at && !user.confirmed_at) {
    throw new Error("Please confirm your Google account email before accessing the CRM.");
  }

  const existing = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
  if (existing.error) throw existing.error;
  if (!existing.data || existing.data.active === false) {
    throw new Error("Your account is not authorized. Please contact admin.");
  }
  return mapProfile(existing.data);
}

export async function getSession() {
  const supabase = getSupabase();
  if (!supabase) return null;
  const result = await supabase.auth.getSession();
  if (result.error) throw result.error;
  return result.data.session;
}

export async function signInWithGoogle() {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase is not configured.");
  const result = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: oauthRedirectUrl() } });
  if (result.error) throw result.error;
}

export async function signOut() {
  const supabase = getSupabase();
  if (supabase) await supabase.auth.signOut();
}

export async function loadState() {
  const supabase = getSupabase();
  const session = await getSession();
  if (!supabase || !session) return localLoad();
  const profile = await ensureProfile(session);
  const [profiles, master, records] = await Promise.all([
    supabase.from("profiles").select("*").order("role").order("full_name"),
    supabase.from("master_data").select("*").order("key").order("sort_order"),
    supabase.from("crm_records").select("*").order("updated_at", { ascending: false })
  ]);

  [profiles, master, records].forEach((result) => {
    if (result.error) throw result.error;
  });

  const next: CrmState = {
    ...defaultState(),
    users: (profiles.data || []).map(mapProfile),
    leads: [],
    requirements: [],
    properties: [],
    interestedProperties: [],
    viewings: [],
    activities: [],
    opportunities: [],
    commissions: [],
    dashboardLayouts: [],
    notifications: [],
    auditLogs: [],
    masterData: { ...masterDefaults }
  };
  if (profile && !next.users.some((user) => user.id === profile.id)) next.users.unshift(profile);
  (master.data || []).forEach((row) => {
    if (!next.masterData[row.key]) next.masterData[row.key] = [];
    if (!next.masterData[row.key].includes(row.value)) next.masterData[row.key].push(row.value);
  });
  (records.data || []).forEach((row) => {
    const collection = REVERSE_COLLECTIONS[row.entity];
    if (collection) {
      (next[collection] as unknown[]).push(row.payload);
    }
  });
  return normalizeState(next);
}

function recordRow(entity: string, item: { id: string; agentId?: string; teamId?: string; leadId?: string; createdAt?: string; updatedAt?: string }, state: CrmState, userId: string) {
  const lead = item.leadId ? state.leads.find((candidate) => candidate.id === item.leadId) : undefined;
  const now = new Date().toISOString();
  return {
    id: item.id,
    entity,
    team_id: item.teamId || lead?.teamId || null,
    agent_id: item.agentId || lead?.agentId || userId || null,
    payload: item,
    created_by: userId || null,
    updated_by: userId || null,
    created_at: item.createdAt || now,
    updated_at: item.updatedAt || now
  };
}

export async function saveState(state: CrmState) {
  localSave(state);
  const supabase = getSupabase();
  const session = await getSession();
  if (!supabase || !session) return;
  const profile = await ensureProfile(session);
  const userId = profile?.id || session.user.id;

  const profileRows = state.users.filter((user) => /^[0-9a-f-]{36}$/i.test(user.id)).map(profileRow);
  if (profileRows.length) {
    const profiles = await supabase.from("profiles").upsert(profileRows, { onConflict: "id" });
    if (profiles.error) throw profiles.error;
  }

  const masterRows = Object.entries(state.masterData).flatMap(([key, values]) => values.map((value, index) => ({ key, value, sort_order: (index + 1) * 10 })));
  const deleted = await supabase.from("master_data").delete().neq("key", "__never__");
  if (deleted.error) throw deleted.error;
  if (masterRows.length) {
    const master = await supabase.from("master_data").insert(masterRows);
    if (master.error) throw master.error;
  }

  const rows = Object.entries(COLLECTIONS).flatMap(([collection, entity]) =>
    (state[collection as keyof typeof COLLECTIONS] as { id: string }[]).map((item) => recordRow(entity, item, state, userId))
  );
  if (rows.length) {
    const records = await supabase.from("crm_records").upsert(rows, { onConflict: "id" });
    if (records.error) throw records.error;
  }
}

export function addAudit(state: CrmState, action: string, entity: string, entityId: string, by: string, previousValue?: unknown, newValue?: unknown) {
  return {
    ...state,
    auditLogs: [{ id: id("audit"), action, entity, entityId, at: new Date().toISOString(), by, previousValue, newValue }, ...state.auditLogs].slice(0, 300)
  };
}
